import {
  Accordion,
  Anchor,
  Blockquote,
  Button,
  ColorInput,
  CopyButton,
  Divider,
  Group,
  InputLabel,
  LoadingOverlay,
  Paper,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  Link,
  useNavigate,
  useParams,
  useRouteLoaderData,
} from "react-router-dom";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useEffect, useState } from "react";
import ImageGetter from "../../resellers/ImageGetter";
import BackButton from "../../../../components/View/BackButton";
import EmailAccountSelector from "../../../../components/Selectors/EmailAccountSelector";
import {
  createReseller,
  deleteReseller,
  editReseller,
} from "../../../../api/UserAPI";
import { notifications } from "@mantine/notifications";
import { getEmails } from "../../../../api/EmailAPI";
import ResellerExtension from "../../resellers/howto/ResellerExtension";
import { getStatusIcon } from "../../resellers/DomainsCell";
import { parseDate } from "../../../../functions/dates";
import {
  IconBell,
  IconBrush,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconLink,
  IconMail,
  IconPlus,
  IconPuzzle,
  IconSettings,
  IconWorld,
} from "@tabler/icons-react";
import ResellerMessages from "./ResellerMessages";
import { getPermission } from "../../../../functions/utilFunctions";
import ResellerLeadsSettings from "./ResellerLeadsSettings";
import ResellersVideos from "./ResellersVideos";
import ResellersDefaultTags from "./ResellersDefaultTags";
import ResellersOptions from "./ResellersOptions";

let customStatusesId = 0;

const ResellerSettings = () => {
  const lang = useLanguage((s) => s.language);
  const { resellerId } = useParams();
  const resellerProfile = useRouteLoaderData("reseller");
  const [integrationSmtp, setIntegrationSmtp] = useState(
    resellerProfile?.integration_id
      ? { id: resellerProfile?.integration_id }
      : null
  );
  const [logo, setLogo] = useState(null);
  const [logoMenu, setLogoMenu] = useState(null);
  const [favicon, setFavicon] = useState(null);

  const [defaultColumns, setDefaultColumns] = useState(
    resellerProfile?.prm_default_custom_statuses
      ?.sort((a, b) => a?.sorting - b?.sorting)
      ?.map((x) => ({
        ...x,
        id: customStatusesId++,
      })) || []
  );
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState(resellerProfile?.tags_default || []);

  const navigate = useNavigate();
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    formData.set("color_primary", data.color_primary.substring(1));
    formData.set("color_secondary", data.color_secondary.substring(1));
    formData.set("color_muted", data.color_muted.substring(1));

    const emailNotificationsEmail = data.emails_notification_email.split(",");
    const emailNotificationsLinkedin =
      data.emails_notification_linkedin.split(",");
    const emailNotificationsGlobal = data.emails_notification_global.split(",");

    emailNotificationsEmail.forEach((value) => {
      formData.append("emails_notification_email[]", value);
    });
    emailNotificationsLinkedin.forEach((value) => {
      formData.append("emails_notification_linkedin[]", value);
    });
    emailNotificationsGlobal.forEach((value) => {
      formData.append("emails_notification_global[]", value);
    });

    formData.set("logo", logo);
    formData.set("logo_menu", logoMenu);
    formData.set("favicon", favicon);
    formData.append("integration_id", integrationSmtp?.id || null);
    formData.append(
      "prm_default_custom_statuses",
      JSON.stringify(
        defaultColumns.map((x) => ({ name: x.name, color: x.color }))
      )
    );
    tags.forEach((tag) => {
      formData.append("tags_default[]", tag);
    });

    setLoading(true);

    const main = resellerId ? editReseller : createReseller;
    main({ id: parseInt(resellerId), formData })
      .then((response) => {
        notifications.show({
          message: resellerId
            ? lang.resellers.row_highlight.updated
            : lang.resellers.row_highlight.created,
          color: "tertiary.8",
        });
        navigate(
          "../?highlight=" +
            (response?.data?.reseller_id || resellerProfile?.id)
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [deleting, setDeleting] = useState(false);
  const handleDelete = () => {
    setDeleting(true);
    deleteReseller({ id: resellerId })
      .then((res) => {
        navigate("/resellers");
      })
      .finally(() => setDeleting(false));
  };

  const handleAddCustom = () => {
    setDefaultColumns((prev) => [
      ...prev,
      {
        id: customStatusesId++,
        name: lang.prm.status.new,
        color: "000000",
        defaultEdit: true,
      },
    ]);
  };

  useEffect(() => {
    getEmails();
  }, []);

  return (
    <Stack gap={"lg"} mt={32}>
      <Stack
        bg={"white"}
        component={"form"}
        onSubmit={handleSubmit}
        pos={"relative"}
      >
        <LoadingOverlay
          visible={loading}
          w={"100%"}
          h={"100%"}
          overlayProps={{
            blur: 15,
          }}
          // loaderProps={{
          //   children: <StepLoad mt={64} />,
          // }}
        />
        <Paper component={Group} p={"md"} gap={"xl"} px={"xl"}>
          <Group>
            <IconSettings />
            <Text size="xl" fw={700}>
              {lang.resellers.create.dialog.name}
            </Text>
          </Group>
          <TextInput
            name="name"
            defaultValue={resellerProfile?.name}
            required
            autoFocus
            size="lg"
            w={300}
          />

          <Text ml={"auto"}>
            {lang.white_labels.created_on}{" "}
            {parseDate(resellerProfile.created_on)}
          </Text>
        </Paper>

        <Divider opacity={0.5} />

        <Paper component={Stack} p={"md"} gap={"md"} px={"xl"}>
          <Group>
            <IconWorld />
            <Text size="xl" fw={700}>
              {lang.white_labels.domains}
            </Text>
          </Group>
          <Blockquote
            radius={"md"}
            color="primary"
            styles={{
              root: {
                padding: 12,
              },
            }}
            component={Group}
          >
            <Text>
              {lang.white_labels.domains_text}
              <CopyButton value={"resellers-pool1.magileads.net"}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? lang.global.copied : lang.global.copy}
                    color={copied ? "green" : "black"}
                  >
                    <Anchor
                      c={"black"}
                      onClick={copy}
                      truncate="end"
                      fw={700}
                      ml={12}
                    >
                      {"resellers-pool1.magileads.net"}
                    </Anchor>
                  </Tooltip>
                )}
              </CopyButton>
            </Text>

            <Text fw={600}>{lang.white_labels.domains_warning}</Text>
          </Blockquote>

          <Group w={"100%"} justify="space-between">
            {resellerProfile?.domain_saas && (
              <TextInput
                name="domain_saas"
                label={lang.resellers.create.dialog.domain_saas_old}
                placeholder={"ex: app.example.com"}
                // description={lang.resellers.domain_saas_desc}
                defaultValue={resellerProfile?.domain_saas}
                w={"23%"}
                size="md"
                rightSection={
                  resellerProfile?.domain_saas
                    ? getStatusIcon(resellerProfile?.domain_saas_status)
                    : null
                }
              />
            )}

            {resellerProfile?.domain_saas2 && (
              <TextInput
                name="domain_saas2"
                label={lang.resellers.create.dialog.domain_saas}
                placeholder={"ex: app.example.com"}
                // description={lang.resellers.domain_saas_desc}
                defaultValue={resellerProfile?.domain_saas2}
                w={"23%"}
                size="md"
                rightSection={
                  resellerProfile?.domain_saas2
                    ? getStatusIcon(resellerProfile?.domain_saas2_status)
                    : null
                }
              />
            )}
            <TextInput
              name="domain_saas3"
              label={
                resellerProfile?.domain_saas2
                  ? lang.resellers.create.dialog.domain_saas_new
                  : lang.resellers.create.dialog.platform_link
              }
              placeholder={"ex: app.example.com"}
              defaultValue={resellerProfile?.domain_saas3}
              w={"23%"}
              size="md"
              rightSection={
                resellerProfile?.domain_saas3
                  ? getStatusIcon(resellerProfile?.domain_saas3_status)
                  : null
              }
            />
            <TextInput
              name="domain_files"
              label={lang.resellers.create.dialog.domain_files}
              placeholder={"ex: file.example.com"}
              defaultValue={resellerProfile?.domain_files}
              // description={lang.resellers.domain_files_desc}
              w={"23%"}
              size="md"
              rightSection={
                resellerProfile?.domain_files
                  ? getStatusIcon(resellerProfile?.domain_files_status)
                  : null
              }
            />
            <TextInput
              name="domain_redirect"
              label={lang.resellers.create.dialog.domain_redirect}
              defaultValue={resellerProfile?.domain_redirect}
              placeholder={"ex: redirect.example.com"}
              // description={lang.resellers.domain_redirect_desc}
              w={"23%"}
              size="md"
              rightSection={
                resellerProfile?.domain_redirect
                  ? getStatusIcon(resellerProfile?.domain_redirect_status)
                  : null
              }
            />
            <TextInput
              name="domain_api"
              label={lang.resellers.create.dialog.domain_api}
              placeholder={"ex: api.example.com"}
              defaultValue={resellerProfile?.domain_api}
              // description={lang.resellers.domain_api_desc}
              w={"23%"}
              size="md"
              rightSection={
                resellerProfile?.domain_api
                  ? getStatusIcon(resellerProfile?.domain_api_status)
                  : null
              }
            />
          </Group>
          <Anchor
            href={
              lang.id === "FRA"
                ? "https://support.magileads.net/article/espace-revendeur"
                : "https://support.magileads.net/en-us/article/cname-configuration"
            }
            target="_blank"
          >
            {lang.resellers.need_help}
          </Anchor>
        </Paper>

        <Divider opacity={0.5} />

        <Paper component={Group} p={"md"} gap={"xl"} px={"xl"}>
          <Group>
            <IconPuzzle />
            <Text size="xl" fw={700}>
              {lang.white_labels.chrome_ext}
            </Text>
          </Group>

          <Group>
            {resellerProfile && (
              <ResellerExtension
                id={resellerProfile.id}
                name={resellerProfile.name}
              />
            )}
          </Group>
          <TextInput
            name="link_chrome_extension"
            // label={lang.resellers.link_extension}
            type="url"
            size="md"
            w={400}
            placeholder="https://chromewebstore.google.com/..."
            defaultValue={resellerProfile?.link_chrome_extension}
            // disabled={!resellerProfile?.link_chrome_extension}
          />
        </Paper>

        <Divider opacity={0.5} />

        <Paper component={Stack} p={"md"} gap={"md"} px={"xl"}>
          <Group>
            <IconBrush />
            <Text size="xl" fw={700}>
              {lang.resellers.branding}
            </Text>
          </Group>

          <Text size="sm">{lang.resellers.branding_text}</Text>
          <Group justify="space-between" align="start">
            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.logo}
              title={
                <Group gap={"xs"}>
                  <InputLabel fw={500} fz={15} required>
                    {lang.white_labels.logo_main}
                  </InputLabel>
                </Group>
              }
              name={"logo"}
              types={["JPG", "JPEG", "PNG"]}
              defaultPreview={resellerProfile?.logo_url}
              setFile={setLogo}
              required
              w={"23%"}
            />

            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.logo_menu}
              title={<Text fw={500}>{lang.white_labels.logo_menu}</Text>}
              name={"logo_menu"}
              types={["JPG", "JPEG", "PNG"]}
              defaultPreview={resellerProfile?.logo_menu_url}
              setFile={setLogoMenu}
              w={"23%"}
            />

            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.favicon}
              title={<Text fw={500}>Favicon (.ico) </Text>}
              name={"favicon"}
              defaultPreview={resellerProfile?.favicon_url}
              types={["ICO"]}
              isIco
              setFile={setFavicon}
              w={"23%"}
            />

            <Stack w={"23%"} gap="xs">
              <ColorInput
                name="color_primary"
                label={lang.resellers.create.dialog.primary_color}
                defaultValue={
                  resellerProfile?.color_primary
                    ? `#${resellerProfile.color_primary}`
                    : "#FDB713"
                }
                format="hex"
                swatches={[
                  "#2e2e2e",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
              <ColorInput
                name="color_secondary"
                label={lang.resellers.create.dialog.secondary_color}
                defaultValue={
                  resellerProfile?.color_secondary
                    ? `#${resellerProfile.color_secondary}`
                    : "#3f51b5"
                }
                format="hex"
                swatches={[
                  "#2e2e2e",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
              <ColorInput
                name="color_muted"
                label={lang.resellers.create.dialog.header_color}
                defaultValue={
                  resellerProfile?.color_muted
                    ? `#${resellerProfile.color_muted}`
                    : "#1a2130"
                }
                format="hex"
                swatches={[
                  "#2e2e2e",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
            </Stack>
          </Group>
        </Paper>

        <Divider opacity={0.5} />

        <Paper component={Group} p={"md"} gap={"xl"} px={"xl"}>
          <Stack w={300} mr={32}>
            <Group>
              <IconMail />
              <Text size="xl" fw={700}>
                {lang.resellers.create.dialog.integration_smtp}
              </Text>
            </Group>

            <Text size="sm">{lang.resellers.integration_smtp_text}</Text>
          </Stack>
          <Stack w={400} gap={4}>
            <EmailAccountSelector
              size={"md"}
              value={integrationSmtp?.id}
              onChange={(v) => setIntegrationSmtp(v || null)}
              smtpOnly="semi"
              w={400}
            />
            {integrationSmtp?.smtp_host === "ssl0.ovh.net" && (
              <Text c={"red"}>{lang.resellers.integration_smtp_ovh_free}</Text>
            )}
          </Stack>
          <Button
            component={Link}
            leftSection={<IconPlus size={18} />}
            to="/senders/email/none"
            target="_blank"
            variant="subtle"
          >
            {lang.resellers.integration_smtp_add}
          </Button>
        </Paper>

        <Divider opacity={0.5} />

        <Paper component={Group} p={"md"} gap={"xl"} px={"xl"}>
          <Stack w={300} mr={32}>
            <Group>
              <IconLink />
              <Text size="xl" fw={700}>
                {lang.resellers.home_link_title}
              </Text>
            </Group>

            <Text size="sm">{lang.resellers.home_link_text}</Text>
          </Stack>

          <TextInput
            name="pricing_url"
            label={lang.home.bottom.help.pricing_url}
            placeholder={"https://www.website.com/pricing"}
            defaultValue={resellerProfile?.pricing_url}
            w={"23%"}
            size="md"
          />
          <TextInput
            name="book_demo"
            label={lang.home.bottom.help.button1}
            placeholder={"https://www.website.com/book-demo"}
            defaultValue={resellerProfile?.book_demo}
            w={"23%"}
            size="md"
          />
          <TextInput
            name="contact_support"
            label={lang.home.bottom.help.button2}
            placeholder={"https://www.website.com/contact"}
            defaultValue={resellerProfile?.contact_support}
            w={"23%"}
            size="md"
          />
          <TextInput
            name="terms_of_sale_url"
            label={lang.resellers.cgv_link}
            placeholder={lang.resellers.cgv_link_placeholder}
            defaultValue={resellerProfile?.terms_of_sale}
            w={"23%"}
            size="md"
          />
        </Paper>

        <Divider opacity={0.5} />

        <Paper p={"md"} gap={"xl"} px={"xl"}>
          <Group>
            <IconBell />
            <Text size="xl" fw={700}>
              {lang.resellers.notifications}
            </Text>
          </Group>
          <Group justify="space-between" align="start">
            <TagsInput
              label={lang.resellers.create.dialog.email_notification_email}
              description={lang.resellers.notif_email_desc}
              name="emails_notification_email"
              placeholder={lang.global.enter_key_to_add}
              defaultValue={resellerProfile?.emails_notification_email}
              w={"32%"}
              size="md"
            />
            <TagsInput
              label={lang.resellers.create.dialog.email_notification_linkedin}
              description={lang.resellers.notif_linkedin_desc}
              name="emails_notification_linkedin"
              placeholder={lang.global.enter_key_to_add}
              defaultValue={resellerProfile?.emails_notification_linkedin}
              w={"32%"}
              size="md"
            />
            <TagsInput
              label={lang.resellers.create.dialog.email_notification_global}
              description={lang.resellers.notif_all_desc}
              name="emails_notification_global"
              defaultValue={resellerProfile?.emails_notification_global}
              placeholder={lang.global.enter_key_to_add}
              w={"32%"}
              size="md"
            />
          </Group>
        </Paper>

        <Divider opacity={0.5} />

        <ResellerLeadsSettings
          items={defaultColumns}
          setItems={setDefaultColumns}
          handleAdd={handleAddCustom}
        />

        <Divider opacity={0.5} />

        <ResellersDefaultTags tags={tags} setTags={setTags} />

        <Group
          justify="end"
          gap={"xl"}
          pos={"sticky"}
          bottom={0}
          p={"md"}
          bg={"#fff"}
          style={{ borderRadius: 12, zIndex: 8 }}
        >
          <BackButton size="md" />
          <Button
            type="submit"
            color="primary"
            size="md"
            leftSection={<IconDeviceFloppy />}
          >
            {resellerProfile
              ? lang.global.save
              : lang.resellers.create_reseller}
          </Button>
        </Group>
      </Stack>

      <ResellersOptions />

      <ResellersVideos />

      {getPermission("personalizeTransactionalEmails") && <ResellerMessages />}

      <Accordion variant="filled" my={"md"} radius={"md"}>
        <Accordion.Item value="advanced" bg={"#fff"} radius={"md"} px={"lg"}>
          <Accordion.Control bg={"#fff"}>
            <Group>
              <IconExclamationCircle color="red" />
              <Text size="xl" fw={700} c={"red"}>
                {lang.white_labels.deletion}
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack align="start">
              <Text c={"red"} fw={600}>
                {lang.white_labels.deletion_text}
              </Text>

              <Text mb={8}>
                {lang.white_labels.deletion_warning1}{" "}
                <b>{resellerProfile.name}</b> ,{" "}
                {lang.white_labels.deletion_warning2}{" "}
                <b>{lang.white_labels.deletion_warning3}</b>
              </Text>

              <Button
                size="md"
                color="red"
                onClick={handleDelete}
                loading={deleting}
              >
                {lang.white_labels.deletion_label}
              </Button>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export default ResellerSettings;
